/**
 * Tenant AI Usage Service
 * A tenant's OWN AI usage (credits/tokens/requests per user & feature).
 * Tenant-scoped (reads the current tenant); credit conversion done on the backend.
 */

import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

export type CostType = 'credit' | 'dollar';

export interface TenantUsageFilter {
  start_date?: string;          // 'YYYY-MM-DD'
  end_date?: string;
  feature_types?: string[];
  models?: string[];
  status?: 'success' | 'failed';
  user_id?: string;
  search?: string;
  cost_type?: CostType;         // default 'credit'
}

export interface TenantUsageSummary {
  cost_type: CostType;
  credit_rate?: number;
  total_cost: number;           // in the chosen unit (credits by default)
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_requests: number;
  most_used_feature: string | null;
  most_active_user: string | null;
  most_active_user_email: string | null;
  models: string[];              // all distinct models used in the period (for the Model filter)
}

export interface TenantUsageRecord {
  reference_id: string;
  name: string;
  feature_type: string;
  user_id?: string;
  user_name: string;           
  user_email: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost: number;                 // in the chosen unit
  status: 'success' | 'failed';
  date: string;                 // ISO
  // Per-call breakdown for the detail drawer (input/output/cost/time per entry).
  breakdown?: Array<{
    stage: string;
    service_type: string;
    prompt_tokens: number;
    completion_tokens: number;
    cost: number;
    success: boolean;
    time: string;
  }>;
}

export interface TenantUsageRecordsResult {
  items: TenantUsageRecord[];
  total: number;
  cost_type: CostType;
  credit_rate?: number;
}

export interface TenantUsageTrendPoint {
  period: string;
  cost: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  request_count: number;
}

type RecordsQuery = TenantUsageFilter & { skip?: number; limit?: number; sort?: string; order?: 'asc' | 'desc'; is_export?: boolean };
type TrendQuery = TenantUsageFilter & { granularity?: 'daily' | 'weekly' | 'monthly' };

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await apiClient.post<{ success: boolean; data: T }>(url, body);
  if (!res.data?.success) throw res.data;
  return res.data.data;
}

class TenantUsageService {
  getSummary(filter: TenantUsageFilter) {
    return post<TenantUsageSummary>(API_ENDPOINTS.TENANT_USAGE.SUMMARY, filter);
  }
  getRecords(query: RecordsQuery) {
    return post<TenantUsageRecordsResult>(API_ENDPOINTS.TENANT_USAGE.RECORDS, query);
  }
  getTrend(query: TrendQuery) {
    return post<TenantUsageTrendPoint[]>(API_ENDPOINTS.TENANT_USAGE.TREND, query);
  }
}

export const tenantUsageService = new TenantUsageService();
export default tenantUsageService;
